import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, FileText, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "rounded-md border border-white/[0.1] bg-[#0B0D10] px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-[#F25F22]/60";

const STATUS_STYLES = {
  active: "border-[#F25F22]/40 bg-[#F25F22]/10 text-[#F25F22]",
  replied: "border-emerald-400/40 bg-emerald-400/10 text-emerald-400",
  recovered: "border-emerald-500/60 bg-emerald-500/15 text-emerald-300",
  lost: "border-red-400/40 bg-red-400/10 text-red-400",
};

const money = (n) =>
  n ? Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : "—";

export default function EstimatesPanel({ token }) {
  const [estimates, setEstimates] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ customer_id: "", customer_name: "", phone: "", amount: "", service: "" });
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const load = () => {
    axios.get(`${API}/estimates`, { headers }).then((r) => setEstimates(r.data)).catch(() => setEstimates([]));
    axios.get(`${API}/customers`, { headers }).then((r) => {
      setCustomers(r.data);
      setForm((f) => (f.customer_id ? f : { ...f, customer_id: r.data[0]?.id || "" }));
    }).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(
        `${API}/estimates`,
        { ...form, amount: form.amount ? Number(form.amount) : null },
        { headers }
      );
      toast.success("Estimate logged — follow-up texts start tomorrow");
      setForm((f) => ({ ...f, customer_name: "", phone: "", amount: "", service: "" }));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not log estimate");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="estimates-panel" className="grid gap-5 lg:grid-cols-2">
      <div className="rounded-xl border border-white/[0.08] bg-[#12161F] p-6">
        <div className="flex items-center gap-2">
          <Plus className="h-4 w-4 text-[#F25F22]" />
          <h3 className="font-display text-base font-semibold text-slate-50">Log a forgotten estimate</h3>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-slate-500">
          Sent a quote that went quiet? Log it here — Backfill texts the homeowner on day 1, 3, and 7
          until they reply. Every reply lands in the Recovery Engine inbox.
        </p>
        <form onSubmit={add} className="mt-5 space-y-3" data-testid="estimate-form">
          <select
            value={form.customer_id}
            onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
            data-testid="estimate-customer-select"
            className={`${inputCls} w-full`}
          >
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.company_name}</option>
            ))}
          </select>
          <input
            value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
            data-testid="estimate-name-input"
            placeholder="Homeowner name"
            required
            className={`${inputCls} w-full`}
          />
          <input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            data-testid="estimate-phone-input"
            placeholder="Homeowner mobile (e.g. +15551234567)"
            required
            className={`${inputCls} w-full`}
          />
          <div className="grid grid-cols-2 gap-3">
            <input
              type="number"
              min="0"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              data-testid="estimate-amount-input"
              placeholder="Amount ($)"
              className={`${inputCls} w-full`}
            />
            <input
              value={form.service}
              onChange={(e) => setForm({ ...form, service: e.target.value })}
              data-testid="estimate-service-input"
              placeholder="Service (e.g. AC replacement)"
              required
              className={`${inputCls} w-full`}
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="estimate-submit-button"
            className="flex w-full items-center justify-center gap-2 rounded-md bg-[#F25F22] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#FF7539] disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Log Estimate
          </button>
        </form>
      </div>

      <div className="rounded-xl border border-white/[0.08] bg-[#12161F] p-6" data-testid="estimate-list">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-[#F25F22]" />
          <h3 className="font-display text-base font-semibold text-slate-50">Open estimates</h3>
          <button
            onClick={load}
            data-testid="estimates-refresh"
            className="ml-auto rounded-md border border-white/[0.12] p-1.5 text-slate-400 hover:text-slate-100"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
        {estimates === null ? (
          <div className="flex items-center gap-2 p-8 text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : estimates.length === 0 ? (
          <p className="p-8 text-sm text-slate-500" data-testid="estimates-empty">
            No estimates logged yet — forgotten quotes are usually the easiest revenue to recover.
          </p>
        ) : (
          <ul className="mt-4 space-y-2.5">
            {estimates.map((e) => (
              <li key={e.id} data-testid={`estimate-${e.id}`} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium text-slate-100">{e.customer_name}</span>
                  <span className="font-mono text-xs text-slate-500">{e.phone}</span>
                  <span className="text-xs text-slate-400">{e.service}</span>
                  <span className="font-mono text-xs text-emerald-400">{money(e.amount)}</span>
                  <span className={`ml-auto rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${STATUS_STYLES[e.status] || ""}`}>
                    {e.status}
                  </span>
                </div>
                <p className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-600">
                  {e.status === "active"
                    ? `Follow-up ${e.follow_count}/3 sent${e.follow_count < 3 ? " · next at day " + [1, 3, 7][e.follow_count] : ""}`
                    : e.status === "replied"
                    ? "Homeowner replied — conversation in Recovery Engine"
                    : e.status}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
